import { ChevronRight as ChevronRightIcon } from '@squareup/dex-icons/dex/control';
import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { Box, Paragraph20, Heading20 } from '@squareup/dex-ui-shared-base';
import { stripMarkdown } from '@squareup/dex-utils-text';
import clsx from 'clsx';
import React, { FunctionComponent, ReactNode } from 'react';

import { Badge, BadgeType } from '../Badge';
import { HttpMethods } from '../HttpMethodString';
import { Link } from '../Link';

import { ApiRoute } from './ApiRoute';
import styles from './reference-card.module.css';

export interface ReferenceCardProps {
  description?: string | undefined;
  title: string | ReactNode;
  httpMethod?: HttpMethods | undefined;
  path?: string | undefined;
  isBeta?: boolean | undefined;
  isV1?: boolean | undefined;
  isDeprecated?: boolean | undefined;
  href?: React.ComponentProps<typeof Link>['href'];
}

const ReferenceCard: FunctionComponent<
  ReferenceCardProps & NullableClassName
> = ({
  className,
  title,
  description,
  httpMethod,
  path,
  isBeta,
  isV1,
  isDeprecated,
  href,
}) => {
  const showBadges = isV1 || isBeta;
  const betaBadge = isBeta ? (
    <Badge size="small" type={BadgeType.Beta} />
  ) : null;
  const v1Badge = isV1 ? <Badge size="small" type={BadgeType.V1} /> : null;
  const badges = showBadges ? (
    <div
      className={styles['reference-card__badges']}
      data-testid="reference-card--badges"
    >
      {betaBadge}
      {v1Badge}
    </div>
  ) : null;
  const chevronRightIcon = showBadges ? null : (
    <ChevronRightIcon
      className={styles['reference-card__chevron-right-icon']}
    />
  );
  const card = (
    <Box
      padding={{ bottom: '2x', left: '3x', right: '3x', top: '2x' }}
      border={{ line: 'standard', radius: '12' }}
      className={clsx(styles['reference-card'], href && className)}
    >
      <Box className={styles['reference-card__details']}>
        <Heading20 margin={{ bottom: '0.5x' }}>{title}</Heading20>
        <Box>
          {httpMethod && path && (
            <ApiRoute
              httpMethod={httpMethod}
              isDeprecated={isDeprecated ?? false}
              path={path}
              testId="reference-card--api-route"
            />
          )}
          {description && (
            <Paragraph20
              className={styles.description}
              data-testid="reference-card--description"
            >
              {stripMarkdown(description)}
            </Paragraph20>
          )}
        </Box>
      </Box>
      {badges}
      {chevronRightIcon}
    </Box>
  );

  return href ? (
    <Link
      trackingId="navigate-to-reference-card"
      focusBorderRadius={'12'}
      className={clsx(styles['reference-link'], className)}
      testId="reference-card--link"
      href={href}
    >
      {card}
    </Link>
  ) : (
    card
  );
};

export { ReferenceCard };
